import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ModalContainer from '@/components/ModalContainer/ModelContainer'
import RequestMaintenanceForm from '@/components/Maintenance/RequestMaintenanceForm/RequestMaintenanceForm'

import CheckboxField from '../../../../../../../components/FormLegacy/FormFieldsV2/CheckboxField'
import PicturesModal from '../../../../../../../components/Modal/PicturesModal'
import { getAssetLink } from '../../../../../../../helpers/get-asset-link'

const getDescription = (checklist, roomItem, t) =>
  t('checklists-dashboard:detailsBody.description', {
    checklistName: checklist.name,
    itemName: roomItem.name,
    answers: roomItem?.questions?.map((question) => question.answer).join(', ') || '-',
    rating:
      roomItem.rating || roomItem.hasIssue ? t('checklists-dashboard:preview.poorLabel') : '-',
    notes: roomItem.notes || '-',
  })

const ChecklistDetailsBody = ({
  selectedRoom,
  checklistStatus,
  isSubmitting,
  revisionMode,
  isGuided,
  updateSelectedRoom,
  checklist,
  refetchDetails,
}) => {
  const { t } = useTranslation(['checklists-dashboard'])
  const statusIsStarted = checklistStatus === 'Started'
  const [picturesToShow, setPicturesToShow] = useState({ data: null, index: 0 })
  const [workOrderModalVisible, setWorkOrderModalVisible] = useState(false)
  const [selectedRoomItemForWorkOrder, setSelectedRoomItemForWorkOrder] = useState(null)

  const changeRoomItemStatusOfRevision = (roomItem) => {
    const updatedSelectedRoom = {
      ...selectedRoom,
      roomItems: selectedRoom.roomItems.map((item) =>
        item.id === roomItem.id
          ? { ...item, revisionRequested: !roomItem.revisionRequested }
          : item,
      ),
    }
    updateSelectedRoom(updatedSelectedRoom)
  }

  return (
    <div className={`main-view-body ${isSubmitting ? 'is-loading' : ''}`}>
      <div className="room-name">{selectedRoom.name}</div>
      {selectedRoom.roomItems.map((item, i) => (
        <div key={i} className="room-item-container">
          <div className="left-side">
            {revisionMode && (
              <CheckboxField
                name={`room-revision-req-id-${item.id}`}
                onChange={() => changeRoomItemStatusOfRevision(item)}
                checked={!!item.revisionRequested}
                disabled={isSubmitting}
              />
            )}
            <div className="title-block">{item.name}</div>
            {isGuided ? (
              <React.Fragment>
                <div className="condition-block-container">
                  <div className="block-header">
                    {t('checklists-dashboard:detailsBody.conditionHeader')}
                  </div>
                  {statusIsStarted ? (
                    <div>{'-'}</div>
                  ) : (
                    <div className={`condition-mark ${item.rating}`}>{item.rating || '-'}</div>
                  )}
                </div>
                <div className="questions-block-container">
                  <div className="block-header">
                    {t('checklists-dashboard:detailsBody.questionsHeader')}
                  </div>
                  <div className="questions-block">
                    {item.questions.map((question) => (
                      <div key={question.id} className="question-container">
                        <div className="question">{question.question}</div>
                        {statusIsStarted ? (
                          <div>{'-'}</div>
                        ) : (
                          <div className={`answer ${question.answer}`}>
                            {question.answer === false
                              ? t('checklists-dashboard:detailsBody.noAnswer')
                              : question.answer === true
                                ? t('checklists-dashboard:detailsBody.yesAnswer')
                                : '-'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <div className="issue-block-container">
                {statusIsStarted ? (
                  <div>{'-'}</div>
                ) : (
                  <div className={`issue-block ${item.hasIssue ? 'issue' : 'no-issue'}`}>
                    {item.hasIssue
                      ? t('checklists-dashboard:detailsBody.issueLabel')
                      : t('checklists-dashboard:detailsBody.noIssueLabel')}
                  </div>
                )}
              </div>
            )}
            <div className="notes-block">
              <span className="block-header">
                {t('checklists-dashboard:detailsBody.notesHeader')}
              </span>
              {statusIsStarted ? (
                <div>{'-'}</div>
              ) : (
                <span className="description-span">{item.notes || '-'}</span>
              )}
            </div>
          </div>
          <div className="images-block">
            {!item.maintenanceId && (item.hasIssue || item.rating === 'Poor') && (
              <div className="create-work-order-block">
                <button
                  className="button button-secondary"
                  onClick={() => {
                    setSelectedRoomItemForWorkOrder(item)
                    setWorkOrderModalVisible(true)
                  }}
                >
                  {t('checklists-dashboard:detailsBody.createWorkOrderButton')}
                </button>
              </div>
            )}
            {!statusIsStarted && item.images ? (
              item.images.map((image, index) => (
                <img
                  key={index}
                  style={{
                    backgroundImage: `url(${getAssetLink({ path: 'chitown-images/checklist', file: image })})`,
                    cursor: 'pointer',
                  }}
                  alt=""
                  onClick={() => setPicturesToShow({ data: item.images, index })}
                />
              ))
            ) : (
              <React.Fragment>
                <div className="dummy-image" />
                <div className="dummy-image" />
              </React.Fragment>
            )}
          </div>
        </div>
      ))}
      <PicturesModal
        assetPath="chitown-images/checklist"
        visible={!!picturesToShow.data}
        pictures={picturesToShow.data || []}
        indexToShow={picturesToShow.index}
        onCloseModal={() => setPicturesToShow({ data: null, index: 0 })}
      />
      {workOrderModalVisible && (
        <ModalContainer
          headerTitle={t('checklists-dashboard:detailsBody.createMaintenanceRequestTitle')}
          onClose={() => setWorkOrderModalVisible(false)}
        >
          <RequestMaintenanceForm
            toggleModal={() => setWorkOrderModalVisible(false)}
            inspectionId={selectedRoom.id}
            description={getDescription(checklist, selectedRoomItemForWorkOrder, t)}
            defaultResident={checklist.resident}
            defaultBuilding={checklist.building}
            defaultPhotos={selectedRoomItemForWorkOrder?.images || []}
            checklistRoomItemId={selectedRoomItemForWorkOrder?.id}
            onSuccess={refetchDetails}
          />
        </ModalContainer>
      )}
    </div>
  )
}

ChecklistDetailsBody.propTypes = {
  selectedRoom: PropTypes.object,
  checklistStatus: PropTypes.string,
  isSubmitting: PropTypes.bool,
  revisionMode: PropTypes.bool,
  isGuided: PropTypes.bool,
  updateSelectedRoom: PropTypes.func,
  checklist: PropTypes.object,
  refetchDetails: PropTypes.func,
}

export default ChecklistDetailsBody
